'use client'

import { useEffect, useState } from 'react'
import { scroller } from 'react-scroll'
import { motion } from 'framer-motion'
import { useMediaQuery } from '@mantine/hooks'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import {
  AnimatedSection,
  AnimatedItem,
} from './_components/AnimatedSection'
import {
  benefitDetails,
  industryDetails,
  targetDetails,
  recruitInfoItems,
  processSteps,
  pioneersFaqTabList,
  pioneersFaqDetailList,
} from './_data/ktechpioneers'
import styles from './ktechpioneers.module.scss'
import WarpBackground from './_components/WarpBackground'
import CustomHeader from './_components/PioneersHeader'
import ChallengePreview from './_components/ChallengePreview'
import PioneersModal from './_components/PioneersModal'
import AnnouncementPopup from './_components/AnnouncementPopup'
import ReviewScheduleSlider from './_components/ReviewScheduleSlider'
import PioneersFAQ from './_components/PioneersFAQ'
import PioneersFooter from './_components/PioneersFooter'
export default function KTechPioneersPage() {
  const pathname = usePathname()
  const isMobile = useMediaQuery('(max-width:1279px)')
  const [modalOpen, setModalOpen] = useState<'tech' | 'target' | null>(null)

  useEffect(() => {
    const hash = window.location.hash.replace('#', '')
    if (hash) {
      setTimeout(() => {
        scroller.scrollTo(hash, {
          duration: 800,
          smooth: 'easeInOutQuart',
          offset: -64,
        })
      }, 500)
    }
  }, [])

  return (
    <main className={`relative bg-black ${styles.pageRoot}`}>
      <WarpBackground />
      <div className="relative">
        {/* header */}
        <CustomHeader basePath={pathname} />

        {/* Section1 : Key Visual */}
        <section className="relative flex h-screen max-h-[200vw] items-center justify-center overflow-hidden pc:max-h-screen">
          <div
            className="absolute inset-0 z-0"
            style={{
              maskImage: 'linear-gradient(to bottom, black 60%, transparent)',
              WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent)',
            }}>
            <img
              src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/section1_kv.jpeg"
              className="h-full w-full object-cover opacity-40"
              alt="K-TECH PIONEERS"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050510]/50 via-[#050510]/30 to-transparent" />
          </div>

          <div className="relative z-10 flex flex-col items-center">
            <p className="mb-4 pc:mb-6 text-center text-sm text-white font-semibold pc:text-3xl">
              Bridging Global Talent <br className="block pc:hidden" />
              with Korea’s Leading Corporations
            </p>
            <h3
              className="text-center text-4xl font-strong tracking-tight lg:text-7xl pc:text-6xl"
              style={{ textShadow: '0 0 40px rgba(59, 130, 246, 0.5)' }}>
              <img
                src="https://d35ai18pny966l.cloudfront.net/course/events/ktechpioneers/section1_kv_logo.png"
                className="h-auto w-full max-w-[90vw] object-contain pc:max-w-[60vw]"
                alt="K-TECH PIONEERS"
              />
            </h3>
            <div className="flex mt-6 pc:mt-12 flex-col items-center justify-center gap-1">
              <p className="text-[#3385FF] text-center font-semibold text-sm pc:text-xl">Application Period</p>
              <p className="text-center text-sm text-white leading-snug pc:text-[28px] font-medium">
                04/08/2026 - 04/28/2026<br />15:00(GMT+9)
              </p>
            </div>
          </div>
        </section>

        {/* Section 2 : 협력 과제 */}
        <section className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="w-full">
            <AnimatedSection className="mb-16 flex flex-col max-w-lg pc:max-w-7xl mx-auto px-4 pc:px-6 gap-4 pc:mb-20 pc:gap-6">
              <h3
                className={`${styles.section2_title_gradient} text-center text-3xl font-strong leading-tight pc:text-[64px]`}>
                글로벌 오픈이노베이션 <br className="block pc:hidden" />
                프로젝트
              </h3>
              <p className="break-keep text-center text-sm text-white pc:text-xl">
                ｢K-테크 파이오니어즈｣ 사업은 초격차 과학기술을 보유한 한국 국적 글로벌 스타트업과 <br className="hidden pc:block" />
                국내 산업 및 연구 생태계를 연결하여 수요 기반 기술 사업화를 추진합니다.
              </p>
            </AnimatedSection>

            {/* 협력과제 리스트 노출 */}
            <AnimatedSection>
              <div className="mb-6 pc:mb-12">
                <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">
                  협력 과제
                </h4>
              </div>
              <div className={`${styles.section2_preview_wrap}`}>
                <ChallengePreview />
              </div>
            </AnimatedSection>
          </div>
        </section>

        <div
          className="mx-auto h-0.5 w-full max-w-lg pc:max-w-7xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), rgba(255,255,255,0.8), rgba(255,255,255,0.5), transparent)',
          }}
        />

        {/* Section 3 : 모집 개요 */}
        <section className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="mx-auto w-full max-w-lg px-4 pc:max-w-7xl pc:px-6">
            <div className="mb-12 pc:mb-16">
              <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">
                모집 개요
              </h4>
            </div>

            <div className="flex w-full flex-col justify-center gap-3 pc:flex-row pc:items-stretch pc:gap-4">
              {recruitInfoItems.map((item, idx) => (
                <AnimatedItem key={idx} index={idx} className="flex w-full pc:w-auto pc:flex-1">
                  <div
                    className={`${styles.glass} ${styles.glowBorder} flex w-full flex-1 flex-col gap-2 rounded-lg p-6 pc:gap-6`}>
                    <div className="flex w-full items-center gap-2 border-b border-white/50 pb-2 pc:pb-6">
                      <img src={item.icon} className="w-8 h-auto object-contain" alt="-" />
                      <h3 className="text-lg font-strong text-[#3385FF] pc:text-2xl">
                        {item.title}
                      </h3>
                    </div>
                    <p className="break-keep text-base leading-normal text-white pc:whitespace-pre-line pc:text-xl">
                      {item.desc}
                    </p>
                    {item.modalType && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => setModalOpen(item.modalType)}
                          className="mt-4 rounded-lg bg-[#3385FF] transition-colors hover:bg-blue-700">
                          <img src="https://glob-dev-resource.s3.ap-northeast-2.amazonaws.com/course/events/ktechpioneers/icon_btn_plus.png" className="w-8 h-auto object-contain" alt="-" />
                        </button>
                      </div>
                    )}
                  </div>
                </AnimatedItem>
              ))}
            </div>
          </div>
        </section>

        <div
          className="mx-auto h-0.5 w-full max-w-lg pc:max-w-7xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), rgba(255,255,255,0.8), rgba(255,255,255,0.5), transparent)',
          }}
        />

        {/* Section 4 : benefit */}
        <section
          id="benefit"
          className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="mx-auto w-full max-w-lg px-4 pc:max-w-7xl pc:px-6">
            <AnimatedSection className="mb-12 pc:mb-16">
              <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">
                참가 혜택
              </h4>
            </AnimatedSection>

            <div className="grid grid-cols-1 gap-4 pc:flex pc:flex-row pc:flex-wrap pc:items-stretch pc:justify-center pc:gap-6">
              {benefitDetails.map(benefit => (
                <AnimatedItem key={benefit.title}
                  className="w-full pc:w-[calc(33.33%-16px)]">
                  <div
                    className={`${styles.glass} ${styles.glowBorder} flex h-full flex-col gap-3 w-full overflow-hidden rounded-lg p-4 pc:gap-[17px] pc:py-10`}>
                    <div className="flex w-full flex-col items-center gap-3 pc:gap-[17px]">
                      <p className="text-xs max-w-fit border border-[#3385FF] rounded-full py-2 px-3 bg-[rgba(51,133,255,0.20)] text-white pc:text-sm">{benefit.count}</p>
                      <h5 className="text-lg font-strong text-[#3385FF] pc:text-2xl">
                        {benefit.title}
                      </h5>
                    </div>
                    <ul className="flex flex-col items-center justify-center gap-0.5 pc:gap-1">
                      {benefit.descList.map(item => (
                        <li key={item}
                          className="text-center max-w-fit text-sm leading-normal whitespace-pre-line break-keep text-white pc:text-lg">
                          <span aria-hidden className="mr-1.5 pc:mr-2">•</span>
                          {item.split(/(\(현금\/현물\))/).map((part, i) =>
                            part === '(현금/현물)'
                              ? <span key={i} className="text-xs text-[#969696] pc:text-[13px]">{part}</span>
                              : part
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedItem>
              ))}
            </div>
          </div>
        </section>

        <div
          className="mx-auto h-0.5 w-full max-w-lg pc:max-w-7xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), rgba(255,255,255,0.8), rgba(255,255,255,0.5), transparent)',
          }}
        />

        {/* Section 5 */}
        <section className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="mx-auto w-full max-w-lg overflow-hidden pc:max-w-7xl pc:px-6">
            <AnimatedSection className="mb-12 pc:mb-16">
              <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">
                심사 일정
              </h4>
            </AnimatedSection>

            <div className={`${styles.section5_slide_wrap}`}>
              <ReviewScheduleSlider />
            </div>
            <div className="mt-10 pc:mt-20">
              <p className="text-sm text-white/50 text-center leading-normal">* 대면 심사 단계에서 현지 평가 비권역은 온라인 발표<br />* 대면 심사 권역은 변경될 수 있음 (사전 공지 예정)</p>
            </div>
          </div>
        </section>

        <div
          className="mx-auto h-0.5 w-full max-w-lg pc:max-w-7xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), rgba(255,255,255,0.8), rgba(255,255,255,0.5), transparent)',
          }}
        />

        {/* Section 6 */}
        <section className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="mx-auto w-full max-w-lg px-4 pc:max-w-7xl pc:px-6">
            <AnimatedSection className="mb-12 pc:mb-16">
              <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">
                4단계 협력 프로세스
              </h4>
            </AnimatedSection>

            <div className="relative">
              {/* PC: 중앙 세로선 / Mobile: 왼쪽 세로선 */}
              <div className="absolute bottom-0 left-5 top-2.5 h-[calc(100%-100px)] w-0.5 bg-gradient-to-b from-blue-500 via-cyan-500 to-blue-500 pc:left-1/2 pc:top-0 pc:h-[calc(100%-100px)] pc:-translate-x-1/2" />

              {processSteps.map((item, idx) => (
                <AnimatedItem key={idx} index={idx}>
                  {isMobile ? (
                    <div className={`relative ${idx < 3 ? 'mb-8' : ''} flex items-start gap-4`}>
                      <div className="z-10 mt-2.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-sm font-strong text-white shadow-[0_0_20px_rgba(59,130,246,0.5)]">
                        {item.step}
                      </div>
                      <div
                        className={`${styles.glass} ${styles.glowBorder} flex flex-1 flex-col gap-2 rounded-lg p-4`}>
                        <h3 className="text-lg font-semibold text-white">
                          {item.title}
                        </h3>
                        <p className="break-keep text-base text-white/70">{item.desc}</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`relative ${idx < 3 ? 'mb-16' : ''
                        } flex flex-row items-start gap-8`}>
                      {item.side === 'left' ? (
                        <>
                          <div className="w-1/2 pr-12 text-right">
                            <div
                              className={`${styles.glass} ${styles.glowBorder} flex flex-col gap-2 rounded-lg p-6`}>
                              <h3 className="font-semibold text-white text-2xl">
                                {item.title}
                              </h3>
                              <p className="whitespace-pre-line break-keep text-xl text-white/70">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                          <div className="z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-lg font-strong text-white shadow-[0_0_20px_rgba(59,130,246,0.5)]">
                            {item.step}
                          </div>
                          <div className="w-1/2 pl-12" />
                        </>
                      ) : (
                        <>
                          <div className="w-1/2 pr-12" />
                          <div className="z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-lg font-strong text-white shadow-[0_0_20px_rgba(59,130,246,0.5)]">
                            {item.step}
                          </div>
                          <div className="w-1/2 pl-12 text-left">
                            <div
                              className={`${styles.glass} ${styles.glowBorder} flex flex-col gap-2 rounded-lg p-6`}>
                              <h3 className="font-semibold text-white text-2xl">
                                {item.title}
                              </h3>
                              <p className="whitespace-pre-line break-keep text-xl text-white/70">
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </AnimatedItem>
              ))}
            </div>
          </div>
        </section>

        <div
          className="mx-auto h-0.5 w-full max-w-lg pc:max-w-7xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), rgba(255,255,255,0.8), rgba(255,255,255,0.5), transparent)',
          }}
        />

        {/* Section 7 - 타임라인 */}
        <section className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="mx-auto w-full max-w-lg px-4 pc:max-w-7xl pc:px-6">
            <AnimatedSection className="mb-12 flex flex-col items-center justify-center gap-1 pc:mb-16 pc:gap-2">
              <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">사업 타임라인</h4>
              <p className="text-center text-base text-white/70 pc:text-xl">(2026년 기준)</p>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              {isMobile ? (
                <div className="relative flex flex-col pl-8">
                  {/* 세로 화살표 라인 */}
                  <motion.div
                    className="absolute -top-8 bottom-0 left-[11px] w-[2px]"
                    style={{
                      background:
                        'linear-gradient(180deg, transparent 0%, #0061F9 10%, #0061F9 100%)',
                      transformOrigin: 'top center',
                    }}
                    initial={{ scaleY: 0 }}
                    whileInView={{ scaleY: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                  />
                  {/* 화살표 끝 */}
                  <motion.span
                    className="absolute bottom-0 left-[5.5px] block h-0 w-0"
                    style={{
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '10px solid #0061F9',
                    }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.9 }}
                  />

                  {[
                    { month: '3월-4월', label: '인재·팀 모집', color: '#0061F9', delay: 0 },
                    { month: '5월', label: '오디션 및 매칭', color: '#00337C', delay: 0.15 },
                    { month: '6월-11월', label: '프로젝트 실행', color: '#0061F9', delay: 0.3 },
                    { month: '12월', label: '데모데이 및 평가', color: '#00337C', delay: 0.45 },
                  ].map((item, i) => (
                    <div key={i} className="relative flex items-center pb-10">
                      <motion.span
                        className="absolute -left-[26px] top-[16px] h-3 w-3 rounded-full bg-[#0061F9]"
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: item.delay + 0.2 }}
                      />
                      <motion.span
                        className="mr-4 w-14 shrink-0 text-sm text-white/70"
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: item.delay + 0.1 }}>
                        {item.month}
                      </motion.span>
                      <motion.div
                        className="flex-1 overflow-hidden rounded px-4 py-3"
                        style={{ backgroundColor: item.color, transformOrigin: 'left center' }}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.6, ease: 'easeOut', delay: item.delay }}>
                        <motion.p
                          className="text-sm text-white"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: item.delay + 0.4 }}>
                          {item.label}
                        </motion.p>
                      </motion.div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="mb-16 block">
                    <div className="mb-8 flex flex-row items-center justify-center">
                      {['3월', '4월', '5월', '6월', '7월'].map((month, i) => (
                        <motion.div
                          key={month}
                          className="flex-1"
                          initial={{ opacity: 0, y: -10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: i * 0.1 }}>
                          <p className="text-center text-base text-white">{month}</p>
                        </motion.div>
                      ))}
                    </div>
                    <div className="relative flex h-5 flex-row items-start justify-center">
                      {[0, 1, 2, 3, 4].map(i => (
                        <div key={i} className="relative flex-1">
                          <motion.span
                            className="absolute -left-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[#0061F9]"
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: i * 0.1 + 0.3 }}
                          />
                          <motion.div
                            className="absolute -left-[1px] top-0 flex h-[125px] w-[2px] items-center"
                            style={{
                              background: 'linear-gradient(360deg, transparent, #0061F9, #0061F9)',
                              transformOrigin: 'top center',
                            }}
                            initial={{ scaleY: 0 }}
                            whileInView={{ scaleY: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 + 0.5 }}
                          />
                        </div>
                      ))}
                      <motion.div
                        className="absolute -left-[70px] top-0 flex h-[2px] items-center"
                        style={{
                          width: 'calc(100% + 120px)',
                          background:
                            'linear-gradient(90deg, transparent 0%, #0061F9 15%, #0061F9 100%)',
                          transformOrigin: 'left center',
                        }}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}>
                        <span
                          className="absolute right-0 block h-0 w-0"
                          style={{
                            borderTop: '6px solid transparent',
                            borderBottom: '6px solid transparent',
                            borderLeft: '10px solid #0061F9',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </motion.div>
                    </div>
                    <div className="relative z-[2] flex flex-row items-center justify-center">
                      <motion.div
                        className="flex-1 overflow-hidden rounded bg-[#0061F9] py-4"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0 }}
                        style={{ transformOrigin: 'left center' }}>
                        <motion.p
                          className="text-center text-base pc:text-xl text-white"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.6 }}>
                          인재·팀 모집
                        </motion.p>
                      </motion.div>
                      <motion.div
                        className="max-w-[20%] flex-1 overflow-hidden rounded bg-[#00337C] py-4"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
                        style={{ transformOrigin: 'left center' }}>
                        <motion.p
                          className="text-center text-base pc:text-xl text-white"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.8 }}>
                          오디션 및 매칭
                        </motion.p>
                      </motion.div>
                      <motion.div
                        className="flex-1 overflow-hidden rounded bg-[#0061F9] py-4"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
                        style={{ transformOrigin: 'left center' }}>
                        <motion.p
                          className="text-center text-base pc:text-xl text-white"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 1.0 }}>
                          프로젝트 실행
                        </motion.p>
                      </motion.div>
                    </div>
                  </div>
                  <div className="block">
                    <div className="mb-8 flex flex-row items-center justify-center">
                      {['8월', '9월', '10월', '11월', '12월'].map((month, i) => (
                        <motion.div
                          key={month}
                          className="flex-1"
                          initial={{ opacity: 0, y: -10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: i * 0.1 }}>
                          <p className="text-center text-base text-white">{month}</p>
                        </motion.div>
                      ))}
                    </div>
                    <div className="relative flex h-5 flex-row items-start justify-center">
                      {[0, 1, 2, 3, 4].map(i => (
                        <div key={i} className="relative flex-1">
                          <motion.span
                            className="absolute -left-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[#0061F9]"
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.3, delay: i * 0.1 + 0.3 }}
                          />
                          <motion.div
                            className="absolute -left-[1px] top-0 flex h-[125px] w-[2px] items-center"
                            style={{
                              background: 'linear-gradient(360deg, transparent, #0061F9, #0061F9)',
                              transformOrigin: 'top center',
                            }}
                            initial={{ scaleY: 0 }}
                            whileInView={{ scaleY: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.1 + 0.5 }}
                          />
                        </div>
                      ))}
                      <motion.div
                        className="absolute -left-[70px] top-0 flex h-[2px] items-center"
                        style={{
                          width: 'calc(100% + 120px)',
                          background:
                            'linear-gradient(90deg, transparent 0%, #0061F9 15%, #0061F9 100%)',
                          transformOrigin: 'left center',
                        }}
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}>
                        <span
                          className="absolute right-0 block h-0 w-0"
                          style={{
                            borderTop: '6px solid transparent',
                            borderBottom: '6px solid transparent',
                            borderLeft: '10px solid #0061F9',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          }}
                        />
                      </motion.div>
                    </div>
                    <div className="relative z-[2] flex flex-row items-center justify-center">
                      <motion.div
                        className="flex-1 overflow-hidden rounded bg-[#0061F9] py-4"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0 }}
                        style={{ transformOrigin: 'left center' }}>
                        <motion.p
                          className="text-center text-base pc:text-xl text-white"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.6 }}>
                          프로젝트 실행
                        </motion.p>
                      </motion.div>
                      <motion.div
                        className="max-w-[20%] flex-1 overflow-hidden rounded bg-[#00337C] py-4"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
                        style={{ transformOrigin: 'left center' }}>
                        <motion.p
                          className="text-center text-base pc:text-xl text-white"
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.8 }}>
                          데모데이 및 평가
                        </motion.p>
                      </motion.div>
                    </div>
                  </div>
                </>
              )}
            </AnimatedSection>
            <p className="text-sm text-white/50 text-center mt-10 pc:mt-20">* 상기 일정은 변경될 수 있습니다.</p>
          </div>
        </section>

        <div
          className="mx-auto h-0.5 w-full max-w-lg pc:max-w-7xl"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), rgba(255,255,255,0.8), rgba(255,255,255,0.5), transparent)',
          }}
        />

        {/* Section 8 - 자주 묻는 질문 */}
        <section
          id="faq"
          className="relative z-10 overflow-hidden py-24 pc:py-40">
          <div className="mx-auto w-full max-w-lg px-4 pc:max-w-7xl pc:px-6">
            <AnimatedSection className="mb-12 flex flex-col items-center justify-center gap-1 pc:mb-16 pc:gap-2">
              <h4 className="text-center text-2xl font-strong text-white pc:text-4xl">자주 묻는 질문</h4>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <PioneersFAQ
                contents={pioneersFaqDetailList}
                contentsTabList={pioneersFaqTabList}
              />
            </AnimatedSection>
          </div>
        </section>

        <div
          className="sticky bottom-0 left-0 z-50 flex w-full items-center justify-center px-4 py-2 pc:py-6">
          <div className="mx-auto w-full max-w-lg pc:max-w-7xl pc:w-auto pc:py-4 pc:px-10 px-4 py-3 inline-flex flex-col pc:flex-row gap-3 pc:gap-[30px] items-center justify-center rounded-xl bg-[rgba(206,225,255,0.20)] backdrop-blur-[10px]">
            <p className="text-center font-medium text-sm text-white pc:text-base">기업과 함께 프로젝트를 하고 싶다면?</p>
            <Link
              href="/list"
              className="inline-flex items-center w-full pc:w-auto justify-center rounded-lg bg-[#0080FF] px-4 py-2 pc:px-5 pc:py-3">
              <p className="text-center text-base font-semibold text-white pc:text-lg">
                “K-TECH PIONEERS” 사업 지원하기
              </p>
            </Link>
          </div>
        </div>

        <PioneersFooter />
      </div>

      <AnnouncementPopup />

      <PioneersModal isOpen={modalOpen === 'tech'} onClose={() => setModalOpen(null)} title="모집 분야">
        {industryDetails.map((item, idx) => (
          <li
            key={idx}
            className="flex flex-col gap-4 pc:gap-5 mb-3 last:mb-0">
            <p className="text-lg font-bold text-[#3385FF] pc:text-2xl">{item.title}</p>
            <div className="h-px w-full bg-white/20" />
            <p
              className={`${styles.text_dot} break-keep text-sm leading-normal text-white pc:text-base`}>
              {item.desc}
            </p>
          </li>
        ))}
      </PioneersModal>

      <PioneersModal isOpen={modalOpen === 'target'} onClose={() => setModalOpen(null)} title="지원 대상">
        {targetDetails.map((section, idx) => (
          <li key={idx} className="flex flex-col gap-4 pc:gap-5">
            <p className="text-lg font-bold text-[#3385FF] pc:text-2xl">{section.title}</p>
            <div className="h-px w-full bg-white/20" />
            {section.intro && (
              <p className="flex gap-2 break-keep text-sm leading-relaxed text-white pc:text-base">
                <span aria-hidden className="mt-0.5 select-none leading-none">
                  •
                </span>
                <span className="flex-1">
                  {typeof section.intro === 'string'
                    ? section.intro
                    : section.intro.map((seg, i) => (
                      <span key={i}>
                        <span className={seg.underline ? 'underline' : undefined}>
                          {seg.text}
                        </span>
                        {seg.sup && (
                          <sup className="ml-0.5 text-[0.65em] text-white/50 no-underline">
                            {seg.sup}
                          </sup>
                        )}
                      </span>
                    ))}
                </span>
              </p>
            )}
            {section.lines && (
              <ul className="flex flex-col gap-2">
                {section.lines.map((line, i) => {
                  const indent =
                    line.level === 2 ? 'pl-12' : line.level === 1 ? 'pl-6' : 'pl-0'
                  if (line.tone === 'header') {
                    return (
                      <li
                        key={i}
                        className="mt-2 flex items-center gap-2 break-keep text-sm font-semibold text-white pc:text-base">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 10 10"
                          fill="none"
                          aria-hidden>
                          <path
                            d="M0.900563 8.02632L3.86492 5.0094L0.900563 1.99248L2.10131 0.770677L5.06567 3.78759L8.03002 0.770677L9.23077 1.99248L6.26642 5.0094L9.23077 8.02632L8.03002 9.24812L5.06567 6.2312L2.10131 9.24812L0.900563 8.02632ZM0 4.94361C0 4.64286 0.0906817 4.39536 0.272045 4.20113C0.453408 4.00689 0.712946 3.90977 1.05066 3.90977C1.40088 3.90977 1.66979 4.00689 1.85741 4.20113C2.04503 4.39536 2.13884 4.64286 2.13884 4.94361C2.13884 5.24436 2.04816 5.48872 1.86679 5.67669C1.68543 5.8584 1.41964 5.94612 1.06942 5.93985C0.725453 5.94612 0.459662 5.8584 0.272045 5.67669C0.0906817 5.48872 0 5.24436 0 4.94361ZM3.9212 8.98496C3.92745 8.69048 4.02126 8.44925 4.20263 8.26128C4.39024 8.06704 4.65291 7.96992 4.99062 7.96992C5.32833 7.96992 5.59099 8.06704 5.77861 8.26128C5.97248 8.44925 6.06629 8.69048 6.06004 8.98496C6.06629 9.27945 5.97248 9.52381 5.77861 9.71805C5.59099 9.90602 5.32833 10 4.99062 10C4.65291 10 4.39024 9.90602 4.20263 9.71805C4.02126 9.52381 3.92745 9.27945 3.9212 8.98496ZM3.9212 1.03383C3.92745 0.733083 4.02126 0.488722 4.20263 0.300752C4.39024 0.106516 4.65291 0.00626566 4.99062 0C5.32833 0.00626566 5.59099 0.106516 5.77861 0.300752C5.97248 0.494987 6.06629 0.739348 6.06004 1.03383C6.06629 1.32206 5.97248 1.56328 5.77861 1.75752C5.59099 1.94549 5.32833 2.04261 4.99062 2.04887C4.65291 2.04261 4.39024 1.94549 4.20263 1.75752C4.02126 1.56955 3.92745 1.32832 3.9212 1.03383ZM7.82364 4.94361C7.82989 4.64912 7.92683 4.40476 8.11445 4.21053C8.30206 4.01003 8.5616 3.90977 8.89306 3.90977C9.23077 3.90977 9.49656 4.00689 9.69043 4.20113C9.89056 4.39536 9.99375 4.64286 10 4.94361C9.99375 5.24436 9.89368 5.48872 9.69981 5.67669C9.5122 5.8584 9.24953 5.94612 8.91182 5.93985C8.57411 5.94612 8.30832 5.8584 8.11445 5.67669C7.92683 5.48872 7.82989 5.24436 7.82364 4.94361Z"
                            fill="white"
                          />
                        </svg>
                        <span>{line.text}</span>
                      </li>
                    )
                  }
                  const color = line.tone === 'note' ? 'text-white/60' : 'text-white'
                  return (
                    <li
                      key={i}
                      className={`flex gap-2 break-keep text-sm leading-relaxed pc:text-base ${indent} ${color}`}>
                      {line.tone === 'note' ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="10 10 10 10"
                          fill="none"
                          aria-hidden
                          className="mt-1.5 shrink-0">
                          <path
                            d="M10.9006 18.0263L13.8649 15.0094L10.9006 11.9925L12.1013 10.7707L15.0657 13.7876L18.03 10.7707L19.2308 11.9925L16.2664 15.0094L19.2308 18.0263L18.03 19.2481L15.0657 16.2312L12.1013 19.2481L10.9006 18.0263ZM10 14.9436C10 14.6429 10.0907 14.3954 10.272 14.2011C10.4534 14.0069 10.7129 13.9098 11.0507 13.9098C11.4009 13.9098 11.6698 14.0069 11.8574 14.2011C12.045 14.3954 12.1388 14.6429 12.1388 14.9436C12.1388 15.2444 12.0482 15.4887 11.8668 15.6767C11.6854 15.8584 11.4196 15.9461 11.0694 15.9398C10.7255 15.9461 10.4597 15.8584 10.272 15.6767C10.0907 15.4887 10 15.2444 10 14.9436ZM13.9212 18.985C13.9275 18.6905 14.0213 18.4492 14.2026 18.2613C14.3902 18.067 14.6529 17.9699 14.9906 17.9699C15.3283 17.9699 15.591 18.067 15.7786 18.2613C15.9725 18.4492 16.0663 18.6905 16.06 18.985C16.0663 19.2794 15.9725 19.5238 15.7786 19.718C15.591 19.906 15.3283 20 14.9906 20C14.6529 20 14.3902 19.906 14.2026 19.718C14.0213 19.5238 13.9275 19.2794 13.9212 18.985ZM13.9212 11.0338C13.9275 10.7331 14.0213 10.4887 14.2026 10.3008C14.3902 10.1065 14.6529 10.0063 14.9906 10C15.3283 10.0063 15.591 10.1065 15.7786 10.3008C15.9725 10.495 16.0663 10.7393 16.06 11.0338C16.0663 11.3221 15.9725 11.5633 15.7786 11.7575C15.591 11.9455 15.3283 12.0426 14.9906 12.0489C14.6529 12.0426 14.3902 11.9455 14.2026 11.7575C14.0213 11.5695 13.9275 11.3283 13.9212 11.0338ZM17.8236 14.9436C17.8299 14.6491 17.9268 14.4048 18.1144 14.2105C18.3021 14.01 18.5616 13.9098 18.8931 13.9098C19.2308 13.9098 19.4966 14.0069 19.6904 14.2011C19.8906 14.3954 19.9937 14.6429 20 14.9436C19.9937 15.2444 19.8937 15.4887 19.6998 15.6767C19.5122 15.8584 19.2495 15.9461 18.9118 15.9398C18.5741 15.9461 18.3083 15.8584 18.1144 15.6767C17.9268 15.4887 17.8299 15.2444 17.8236 14.9436Z"
                            fill="white"
                            fillOpacity="0.6"
                          />
                        </svg>
                      ) : (
                        <span aria-hidden className="select-none">
                          •
                        </span>
                      )}
                      <span className="flex-1">{line.text}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </li>
        ))}
      </PioneersModal>
    </main>
  )
}
